import React, { useState, MouseEvent } from "react";
import { PendulumLogo } from "../../../../api/giftCategories";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSGrid from "../../../system-design/DSGrid";
import DSBox from "../../../system-design/DSBox";
import DSTypography from "../../../system-design/DSTypography";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";

interface LogosPickDialogProps {
  onClose: () => void;
  selectedLogo?: string;
  onPickLogo: (selectedLogo: string | undefined) => void;
  logos?: PendulumLogo[];
  logosLoading?: boolean;
}

const LogosPickDialog: React.FC<LogosPickDialogProps> = ({
  onClose,
  selectedLogo,
  onPickLogo,
  logos,
  logosLoading,
}) => {
  const [selected, setSelected] = useState<string | undefined>(selectedLogo);

  const handleToggleSelection = (logoUrl: string) => {
    setSelected((prev) => (prev === logoUrl ? undefined : logoUrl));
  };

  const handleCancel = () => {
    setSelected(selectedLogo);
    onClose();
  };

  const handleSave = (e: MouseEvent) => {
    e.preventDefault();
    onPickLogo(selected);
    onClose();
  };

  return (
    <DSDialog open={true} onClose={handleCancel} fullWidth maxWidth="md">
      <DSDialogTitle>{"Select Pendulum Logo"}</DSDialogTitle>
      <DSDialogContent dividers style={{ height: "60vh", overflow: "hidden" }}>
        {logosLoading ? (
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <DSCircularProgress />
            <DSTypography sx={{ mt: 2 }}>Loading logos...</DSTypography>
          </DSBox>
        ) : logos && logos.length > 0 ? (
          <DSBox sx={{ height: "100%", overflow: "auto", p: 1 }}>
            <DSGrid container spacing={2}>
              {logos.map((logo: PendulumLogo) => (
                <DSGrid item xs={6} sm={4} md={3} key={logo.id}>
                  <DSCard
                    sx={{
                      cursor: "pointer",
                      height: "180px",
                      display: "flex",
                      flexDirection: "column",
                      border:
                        selected === logo.url
                          ? "2px solid #2196F3"
                          : "1px solid #e0e0e0",
                      backgroundColor:
                        selected === logo.url
                          ? "rgba(33, 150, 243, 0.1)"
                          : "white",
                      "&:hover": {
                        backgroundColor:
                          selected === logo.url
                            ? "rgba(33, 150, 243, 0.2)"
                            : "rgba(0, 0, 0, 0.04)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => handleToggleSelection(logo.url)}
                  >
                    <DSCardContent
                      sx={{
                        p: 2,
                        textAlign: "center",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      {logo.url && (
                        <DSBox
                          sx={{
                            mb: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 80,
                          }}
                        >
                          <img
                            src={logo.url}
                            alt={logo.name}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "80px",
                              objectFit: "contain",
                            }}
                          />
                        </DSBox>
                      )}
                      <DSTypography
                        variant="subtitle2"
                        sx={{
                          fontWeight: "bold",
                          mb: 1,
                          fontSize: "0.875rem",
                          lineHeight: 1.2,
                        }}
                      >
                        {logo.name || logo.filename}
                      </DSTypography>
                      {logo.description && (
                        <DSTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {logo.description}
                        </DSTypography>
                      )}
                    </DSCardContent>
                  </DSCard>
                </DSGrid>
              ))}
            </DSGrid>
          </DSBox>
        ) : (
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <DSTypography variant="h6" color="text.secondary">
              No logos available
            </DSTypography>
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={handleCancel}>Cancel</DSSecondaryButton>
        <DSPrimaryButton onClick={handleSave}>
          {selected ? "Select Logo" : "Clear Selection"}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default LogosPickDialog;
